import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { Account } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate, ProjectSelectionHint } from '@/features/projects/ProjectGate';
import { ActivityComposer } from '@/features/activities/ActivityComposer';
import { ActivityTimeline } from '@/features/activities/ActivityTimeline';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from './ProjectsPage.module.css';

export function AccountsPage() {
  const { selectedProjectId } = useProjectContext();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<Account | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createWebsite, setCreateWebsite] = useState('');

  const listQ = useQuery({
    queryKey: ['accounts', selectedProjectId, q],
    enabled: Boolean(selectedProjectId),
    queryFn: () => {
      const params = new URLSearchParams({ project_id: selectedProjectId! });
      if (q.trim()) params.set('q', q.trim());
      return apiFetch<{ accounts: Account[] }>(`/api/accounts?${params}`);
    },
  });

  const createAccount = useMutation({
    mutationFn: () =>
      apiFetch('/api/accounts', {
        method: 'POST',
        body: JSON.stringify({
          project_id: selectedProjectId,
          name: createName.trim(),
          website: createWebsite.trim() || null,
        }),
      }),
    onSuccess: () => {
      setCreateName('');
      setCreateWebsite('');
      setCreateOpen(false);
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  const saveAccount = useMutation({
    mutationFn: (a: Account) =>
      apiFetch(`/api/accounts/${a.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: a.name,
          website: a.website,
          industry: a.industry,
          company_size: a.company_size,
          notes: a.notes,
        }),
      }),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  function submitCreate(e: FormEvent) {
    e.preventDefault();
    if (!createName.trim()) return;
    createAccount.mutate();
  }

  return (
    <ProjectGate pageTitle="Accounts">
      <h1 className={styles.title}>Accounts</h1>
      <ProjectSelectionHint />
      <StateMessage title="CRM B">
        Bedrijfsaccounts gekoppeld aan leads in het geselecteerde project.
      </StateMessage>

      <div className={styles.row}>
        <Field label="Zoeken">
          <TextInput
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Naam of domein"
          />
        </Field>
        <Button onClick={() => setCreateOpen((v) => !v)}>
          {createOpen ? 'Sluiten' : 'Nieuw account'}
        </Button>
      </div>

      {createOpen ? (
        <form className={styles.card} onSubmit={(e) => void submitCreate(e)}>
          <Field label="Naam">
            <TextInput
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              required
            />
          </Field>
          <Field label="Website">
            <TextInput value={createWebsite} onChange={(e) => setCreateWebsite(e.target.value)} />
          </Field>
          {createAccount.isError ? (
            <ErrorState message={(createAccount.error as Error).message} />
          ) : null}
          <Button type="submit" disabled={createAccount.isPending}>
            Opslaan
          </Button>
        </form>
      ) : null}

      {listQ.isLoading ? (
        <LoadingState />
      ) : listQ.isError ? (
        <ErrorState message={(listQ.error as Error).message} />
      ) : !listQ.data?.accounts.length ? (
        <StateMessage title="Geen accounts">Maak een account aan of koppel leads.</StateMessage>
      ) : (
        <ul className={styles.list}>
          {listQ.data.accounts.map((a) => (
            <li key={a.id} className={styles.listItem}>
              <div>
                <strong>{a.name}</strong>
                <div className={styles.mutedSmall}>
                  {a.domain_normalized ?? a.website ?? '—'} · {a.contact_count ?? 0} contact(en)
                </div>
              </div>
              <Button variant="secondary" onClick={() => setEditing({ ...a })}>
                Bewerken
              </Button>
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <form
          className={styles.card}
          onSubmit={(e) => {
            e.preventDefault();
            saveAccount.mutate(editing);
          }}
        >
          <h2>{editing.name}</h2>
          <div className={styles.formGrid}>
            <Field label="Naam">
              <TextInput
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
              />
            </Field>
            <Field label="Website">
              <TextInput
                value={editing.website ?? ''}
                onChange={(e) => setEditing({ ...editing, website: e.target.value || null })}
              />
            </Field>
            <Field label="Branche">
              <TextInput
                value={editing.industry ?? ''}
                onChange={(e) => setEditing({ ...editing, industry: e.target.value || null })}
              />
            </Field>
            <Field label="Grootte">
              <TextInput
                value={editing.company_size ?? ''}
                onChange={(e) => setEditing({ ...editing, company_size: e.target.value || null })}
              />
            </Field>
            <Field label="Notities">
              <TextArea
                rows={3}
                value={editing.notes ?? ''}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value || null })}
              />
            </Field>
          </div>
          {saveAccount.isError ? (
            <ErrorState message={(saveAccount.error as Error).message} />
          ) : null}
          <div className={styles.row}>
            <Button type="submit" disabled={saveAccount.isPending}>
              Opslaan
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Annuleren
            </Button>
          </div>
          {selectedProjectId ? (
            <>
              <h3>Account-timeline</h3>
              <ActivityComposer projectId={selectedProjectId} accountId={editing.id} />
              <ActivityTimeline projectId={selectedProjectId} accountId={editing.id} />
            </>
          ) : null}
        </form>
      ) : null}
    </ProjectGate>
  );
}
